import { GroupType } from '@kcftech/prometheus-client';
import { DropdownTrigger } from 'components/dropdown/DropdownTrigger';
import { ButtonLabel, ButtonLabelText } from 'components/dropdown/styled';
import {
    type FilterMode,
    GroupTreeNode,
    useGroupDropdownTree,
} from 'components/dropdown/useGroupDropdownTree';

export type { FilterMode };

import { StatelessPopup } from 'components/popup/stateless_popup/StatelessPopup';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { useFeatureFlag } from 'contexts/configcat/useFeatureFlag';
import { usePopup } from 'custom_hooks/usePopup';
import { useSet } from 'custom_hooks/useSet';
import {
    TreeItemGroupLabel,
    TreeItemGroupLabelText,
} from 'pages/workbench/side_panel/workbench_tree/styled';
import { TreeItemGroupIcon } from 'pages/workbench/side_panel/workbench_tree/TreeItemGroupIcon';
import { useCallback, useMemo } from 'react';
import useResizeAware from 'react-resize-aware';
import { DataTree } from 'shared/data_tree/DataTree';
import {
    TreeExpansionConfig,
    TreeNodeRenderer,
    TreeSelectionConfig,
} from 'shared/data_tree/types';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';

const SelectNoneOption = styled.button`
    display: flex;
    width: 100%;
    padding: 0.35rem 0.5rem;
    border: none;
    background: none;
    cursor: pointer;
    text-align: left;
    color: ${tokens.text.secondary};
    font-size: 0.875rem;
    border-radius: 4px;

    &:hover {
        background-color: ${tokens.action.hover};
    }

    &:active {
        background-color: ${tokens.action.selected};
    }
`;

const GroupDropdownTreeContainer = styled.div`
    .data-tree-node > label {
        border-radius: 4px;

        &:hover {
            background-color: ${tokens.action.hover};
        }

        &:active {
            background-color: ${tokens.action.selected};
        }
    }

    .data-tree-node > div button {
        border-radius: 4px;

        &:hover {
            background-color: ${tokens.action.hover};
        }

        &:active {
            background-color: ${tokens.action.selected};
        }
    }
`;

type EntityWithIdAndName = {
    Id: string;
    Name: string;
};

export type GroupDropdownProps = Readonly<{
    /**
     * The currently selected group.
     */
    value: EntityWithIdAndName | undefined;
    /**
     * Callback to call when the selected group should change.
     */
    onChange: (group: EntityWithIdAndName) => void;
    /**
     * ID to put on the button which triggers the dropdown, which
     * can be used for associating a form label.
     */
    id?: string;
    /**
     * CSS class to apply to the dropdown trigger button.
     */
    className?: string;
    /**
     * Whether or not the dropdown button can be triggered.
     */
    disabled?: boolean;
    /**
     * Title to put on the dropdown button.
     */
    title?: string;
    /**
     * The location ID from which groups should be selected.
     */
    locationId: string;
    /**
     * The string to show if no group is selected
     * @default 'Select a group'
     */
    defaultValueText?: string;
    /**
     * if 'undefined' should be given an entry in the list
     */
    allowSelectNone?: boolean;

    /**
     * Tree filter mode (defaults to asset and above)
     */
    includedGroupTypes?: FilterMode;
    /**
     * Optional group to disable (along with its children) if present in the available options.
     */
    disabledGroupId?: string;
    style?: React.CSSProperties;
}>;

/**
 * Allows a group (a.k.a. "tree item") to be selected from a list of possible
 * groups under a given location.
 */
export function GroupDropdown({
    id,
    className,
    value,
    onChange,
    disabled,
    title,
    locationId,
    defaultValueText = 'Select a group',
    allowSelectNone,
    includedGroupTypes,
    disabledGroupId,
}: GroupDropdownProps) {
    const { value: timeSeriesCutoverPhase2 } = useFeatureFlag('timeSeriesCutoverPhase2');

    const { isOpen, popupRef, triggerRef, toggleOpen, close } = usePopup();
    const [resizeListener, sizes] = useResizeAware();

    const mode = includedGroupTypes ?? 'assetAndAbove';
    const { tree, isLoading, loadingIds, loadChildren } = useGroupDropdownTree({
        locationId,
        mode,
    });

    const [expandedIds, setExpandedIds] = useSet<string>(() => new Set<string>());

    const isDisabledNode = useCallback(
        (nodeId: string): boolean => {
            if (!disabledGroupId) {
                return false;
            }
            if (nodeId === disabledGroupId) {
                return true;
            }
            if (!tree.has(nodeId)) {
                return false;
            }
            return tree
                .get(nodeId)
                .hierarchy.some((ancestor) => ancestor.id === disabledGroupId);
        },
        [disabledGroupId, tree],
    );

    const renderNode = useCallback<TreeNodeRenderer<GroupTreeNode>>(
        (renderInfo) => {
            const {
                isExpanded,
                node: { data: treeItem },
            } = renderInfo;
            const nodeDisabled = isDisabledNode(treeItem.id);
            return (
                <TreeItemGroupLabel $isSelected={false} $isDisabled={nodeDisabled}>
                    <TreeItemGroupIcon
                        treeItemType={treeItem.type}
                        isCollectorDependentSystem={treeItem.isCollectorDependentSystem}
                        isExpanded={isExpanded}
                        isSelected={false}
                    />
                    <TreeItemGroupLabelText $isRoot={false} title={treeItem.name}>
                        {treeItem.name}
                    </TreeItemGroupLabelText>
                </TreeItemGroupLabel>
            );
        },
        [isDisabledNode],
    );

    const expandable = useMemo<TreeExpansionConfig<GroupTreeNode>>(
        () => ({
            isExpanded: (node) => expandedIds.has(node.id),
            canExpand: (node) =>
                node.data.hasChildren &&
                // In timeSeriesCutoverPhase2, don't allow expanding MPs because they only have
                // IndicatorGroups as children, which we are excluding
                (!timeSeriesCutoverPhase2 ||
                    node.data.type !== GroupType.MonitoringPoint),
            onChange: (event) => setExpandedIds.toggle(event.node.id),
            isLoading: (node) => loadingIds.includes(node.id),
            onLoadChildren: (node) => loadChildren(node.id),
        }),
        [expandedIds, timeSeriesCutoverPhase2, setExpandedIds, loadingIds, loadChildren],
    );

    const selectable = useMemo<TreeSelectionConfig<GroupTreeNode>>(
        () => ({
            selectionStyle: 'custom',
            isSelected: () => false,
            onChange: (event) => {
                if (isDisabledNode(event.node.id)) {
                    return;
                }
                onChange({
                    Id: event.node.data.id,
                    Name: event.node.data.name,
                });
                close();
            },
            canSelect: (node) => !isDisabledNode(node.id),
        }),
        [close, isDisabledNode, onChange],
    );

    return (
        <div>
            <DropdownTrigger
                ref={triggerRef}
                onClick={toggleOpen}
                style={{ position: 'relative' }}
                className={className}
                type="button"
                id={id}
                disabled={disabled}
                title={title}
            >
                {resizeListener}
                <ButtonLabel>
                    <ButtonLabelText $isPlaceholder={value === undefined}>
                        {value?.Name ?? defaultValueText}
                    </ButtonLabelText>
                </ButtonLabel>
            </DropdownTrigger>
            {isOpen && (
                <StatelessPopup
                    onClose={close}
                    ref={popupRef}
                    popupClassName="modal-popup menu-max-height"
                    anchor={triggerRef.current}
                    popupSettings={{
                        style: {
                            width: sizes.width ?? 'auto',
                            marginTop: 2,
                            overflowY: 'auto',
                        },
                    }}
                    noOverlay
                >
                    <SpinnerArea isLoading={isLoading}>
                        <GroupDropdownTreeContainer>
                            {allowSelectNone && (
                                <SelectNoneOption
                                    type="button"
                                    onClick={() => {
                                        onChange({
                                            Id: undefined as unknown as string,
                                            Name: defaultValueText,
                                        });
                                        close();
                                    }}
                                >
                                    {defaultValueText}
                                </SelectNoneOption>
                            )}
                            <DataTree<GroupTreeNode>
                                treeId="group-dropdown-tree"
                                data={tree}
                                renderNode={renderNode}
                                expandable={expandable}
                                selectable={selectable}
                            />
                        </GroupDropdownTreeContainer>
                    </SpinnerArea>
                </StatelessPopup>
            )}
        </div>
    );
}
